import React, { useContext, useMemo, useState } from 'react';

export type TransactionDisplayType = 'payment' | 'transfer';
type ToggleSwitchState = 'disabled' | 'enabled' | 'hidden';
type TransactionContextType = {
    toggleSwitchState: { value: ToggleSwitchState; set: (value: ToggleSwitchState) => void } | undefined;
    active: { value: boolean; set: (value: boolean) => void } | undefined;
    transactionType: { value: TransactionDisplayType; set: (value: TransactionDisplayType) => void } | undefined;
    resetForm: { value: boolean; set: (value: boolean) => void } | undefined;
    hasStartedTransfer: { value: boolean; set: (value: boolean) => void } | undefined;
    hasStartedPayment: { value: boolean; set: (value: boolean) => void } | undefined;
    stopTimeoutOnSuccess: { value: boolean; set: (value: boolean) => void } | undefined;
};

type FocusContext = {
    toggleSwitchState: { value: ToggleSwitchState; set: (value: ToggleSwitchState) => void };
    active: { value: boolean; set: (value: boolean) => void };
    transactionType: { value: TransactionDisplayType; set: (value: TransactionDisplayType) => void };
    resetForm: { value: boolean; set: (value: boolean) => void };
    hasStartedTransfer: { value: boolean; set: (value: boolean) => void };
    hasStartedPayment: { value: boolean; set: (value: boolean) => void };
    stopTimeoutOnSuccess: { value: boolean; set: (value: boolean) => void };
};

const FocusMonitorContext = React.createContext<TransactionContextType>({
    active: undefined,
    toggleSwitchState: undefined,
    transactionType: undefined,
    resetForm: undefined,
    hasStartedTransfer: undefined,
    hasStartedPayment: undefined,
    stopTimeoutOnSuccess: undefined,
});

export const FocusContext: React.FC<{ defaultActive?: boolean } & React.PropsWithChildren> = ({
    children,
    defaultActive = false,
}) => {
    const [active, setActive] = useState(defaultActive);
    const [toggleSwitchState, setToggleSwitchState] = useState<ToggleSwitchState>('enabled');
    const [transactionType, setTransactionType] = useState<TransactionDisplayType>('payment');
    const [resetForm, setResetForm] = useState<boolean>(false);
    const [hasStartedTransfer, setHasStartedTransfer] = useState<boolean>(false);
    const [hasStartedPayment, setHasStartedPayment] = useState<boolean>(false);
    const [stopTimeoutOnSuccess, setStopTimeoutOnSuccess] = useState<boolean>(false);

    const contextValue = useMemo(
        () => ({
            toggleSwitchState: { value: toggleSwitchState, set: setToggleSwitchState },
            active: { value: active, set: setActive },
            transactionType: { value: transactionType, set: setTransactionType },
            resetForm: { value: resetForm, set: setResetForm },
            hasStartedTransfer: { value: hasStartedTransfer, set: setHasStartedTransfer },
            hasStartedPayment: { value: hasStartedPayment, set: setHasStartedPayment },
            stopTimeoutOnSuccess: { value: stopTimeoutOnSuccess, set: setStopTimeoutOnSuccess },
        }),
        [
            toggleSwitchState,
            setToggleSwitchState,
            active,
            setActive,
            transactionType,
            setTransactionType,
            resetForm,
            setResetForm,
            hasStartedTransfer,
            setHasStartedTransfer,
            hasStartedPayment,
            setHasStartedPayment,
            stopTimeoutOnSuccess,
            setStopTimeoutOnSuccess,
        ],
    );

    return <FocusMonitorContext.Provider value={contextValue}>{children}</FocusMonitorContext.Provider>;
};

export default () => {
    const context = useContext(FocusMonitorContext)! as FocusContext;
    if (context === undefined) {
        throw new Error('useFormContext must be used within FocusMonitorContext');
    }
    return context;
};
